import { axiosGet, axiosPatch, axiosPost } from "./axiosService";
import { isExpired, decodeToken } from "react-jwt";
import { LocalStorageKey } from "../constants/localStorageKeys";
import { UtilitiesService } from "./utilitiesService";
import { Roles } from "../constants/roles";
import { Observables } from "../observables";
import { Toaster } from "../shared/components/common/toaster";
import { BASE_URLS, IS_SA } from "../config/appConfig";
import { signOut } from "../auth/authUtilities";
import { identifyOpenPanel } from "../helpers/openPanelHelper";

const authURL = "authentication/";
const loginURL = authURL + "login/";
const forgotPasswordURL = authURL + "forgotpassword/";
const resetPasswordURL = authURL + "resetpassword/";
const adminAuthURL = authURL + "EnableAuthenticator";
const validatePasswordURL = authURL + "passwordvalidate";
const changePasswordUrl = authURL + "changepassword";
const refreshURL = authURL + "refreshtoken/";
const utilitiesService = new UtilitiesService();

const getTokenKeyForCurrentRegion = () => {
	// `IS_SA` is evaluated at module load, but `localStorage.region` can change at runtime.
	// Always read the current region to keep token-slot selection correct.
	const currentRegion = localStorage.getItem('region');
	return currentRegion === 'sa'
		? LocalStorageKey.token_sa
		: LocalStorageKey.token_ae; // default to UAE/global slot
};

export const login = async (credentials) => {
  try {
    const resp = await axiosPost(credentials, loginURL, {
      hide_success_toaster: true,
    });
    if (resp.data && resp.data.token) {
      if (
        resp?.data.roles?.includes(Roles.Borrower) ||
        resp?.data.roles?.includes(Roles.Investor)
      ) {
        setRegistrationInfo(resp.data);
        localStorage.setItem(
          LocalStorageKey.LoginInfo,
          JSON.stringify(resp.data)
        );
        saveToken(resp.data.token);
        const user = getExtractedUserFromToken();
        setUser(user);
        identifyOpenPanel(user);
        return resp;
      } else {
        Toaster({
          msg: `If you are a Admin, Please use ${
            IS_SA ? BASE_URLS.sa : BASE_URLS.en
          } site.`,
        });
        return null;
      }
    }
    return null;
  } catch (e) {
    throw e;
  }
};

export const forgotPassword = (form) => {
  return axiosPost(form, forgotPasswordURL, { hide_toaster: "true" });
};

export const resetPassword = (form) => {
  return axiosPatch(resetPasswordURL, form, { hide_success_toaster: true });
};

export const checkDuplicateEmail = (email) => {
  return axiosGet(null, `authentication/emailused?email=${email}`);
};

export const sendEmailConfirmation = (email) => {
  return axiosPost({ email }, `authentication/EmailConfirmation`);
};

export const confirmEmail = (form) => {
  return axiosPost(form, `authentication/ConfirmEmail`, { hide_toaster: true });
};

export const getAdminAuthQRCode = () => {
  return axiosGet(null, adminAuthURL);
};

export const submitAdminOTPVerificationCode = (form) => {
  return axiosPost(form, adminAuthURL);
};

export const verifyAdminAuth = (form) => {
  return axiosPost(form, "otp/Verify/mfa", {}, "text");
};

const mapTokenPayloadToUser = (tokenPayload) => {
  const isApproved = tokenPayload.approved === "True";
  let OtpVerified = true;
  if (tokenPayload.hasOwnProperty("OtpVerified")) {
    OtpVerified = tokenPayload.OtpVerified === "True";
  }

  return {
    nameid: tokenPayload.nameid,
    email: tokenPayload.email,
    unique_name: tokenPayload.unique_name,
    role: tokenPayload.role,
    isApproved: isApproved,
    status: tokenPayload.status,
    OtpVerified: OtpVerified,
    registrationstep: parseInt(tokenPayload.registrationstep),
    investorGuideStep: tokenPayload.investorGuideStep,
    signUpDate: tokenPayload?.signUpDate || null,
    isKsaInternationalInvestor: tokenPayload.isKsaInternationalInvestor === "True",
  };
};

export const getLoginInfo = () => {
  const loginInfo = localStorage.getItem(LocalStorageKey.LoginInfo);
  return loginInfo ? JSON.parse(loginInfo) : null;
};

export const saveTokenFromRegistration = (token) => {
  if (token) {
    saveToken(token);
    const user = getExtractedUserFromToken();
    setUser(user);
    identifyOpenPanel(user);
  }
};

export const refreshTokenIfNecessary = async (token) => {
  if (!token) {
    return token;
  }

  const expire = decodeToken(token)?.exp;
  const diff = expire * 1000 - Date.now();
  if (diff / 60000 > 4) {
    // If diff > then 4 minute do nothing
    return token;
  }

  if (localStorage.getItem(LocalStorageKey.tokenRefresh) === "1") {
    return token;
  }

  localStorage.setItem(LocalStorageKey.tokenRefresh, "1");

  try {
    const resp = await axiosPost(
      null,
      refreshURL,
      { hide_success_toaster: true },
      "json"
    );
    localStorage.setItem(LocalStorageKey.tokenRefresh, "0");
    if (resp.data) {
      let newToken = resp.data;
      saveToken(newToken);
      localStorage.setItem(LocalStorageKey.tokenExpiring, "0");
      const user = getExtractedUserFromToken();
      setUser(user);
      identifyOpenPanel(user);
      return newToken;
    }
    return token;
  } catch (err) {
    localStorage.setItem(LocalStorageKey.tokenRefresh, "0");
    return token;
  }
};

export const isAdmin = (user) => {
  if (user) {
    return user.roles.find(
      (role) =>
        role === Roles.AccountManager ||
        role === Roles.SuperAdmin ||
        role === Roles.CreditAnalyst
    );
  }
  return false;
};

export const setUser = (user) => {
  utilitiesService.setLocalStorage({
    item: LocalStorageKey.users,
    value: JSON.stringify(user),
  });
};

export const loginUsingToken = (token) => {
  const isTokenExpired = isExpired(token);
  if (isTokenExpired) {
    return null;
  }

  saveToken(token);
  const user = getExtractedUserFromToken();
  setUser(user);
  identifyOpenPanel(user);
};

export const getExtractedUserFromToken = () => {
  const token = getToken();
  if (!token) return null;
  const isTokenExpired = isExpired(token);
  if (isTokenExpired) {
    return null;
  }

  const tokenPayload = decodeToken(token);
  const user = mapTokenPayloadToUser(tokenPayload);
  return user;
};

export const getUser = () => {
  // Derive the user from the token for the *current* platform.
  // This prevents UAE/KSA auth data from leaking across platform switching.
  return getExtractedUserFromToken();
};

export const getFirstName = () => {
  if (userName()) {
    return userName()?.split(" ")[0] || "";
  }
  return "";
};

export const userName = () => {
  if (getUser()) {
    let name = getUser()?.unique_name;
    return name && name?.replace(/ /g, "") && name?.length <= 15
      ? name
      : getUser()?.email;
  }
};

export const saveToken = (token) => {
  utilitiesService.setLocalStorage({
    item: getTokenKeyForCurrentRegion(),
    value: token,
  });
};

export const getToken = () => {
  const tokenKey = getTokenKeyForCurrentRegion();
  const token = utilitiesService.getLocalStorage({ item: tokenKey });
  if (token) return token;

  // Backward-compatible migration: if an old shared token exists, migrate it
  // into the current platform token slot and remove the legacy key.
  const legacyToken = utilitiesService.getLocalStorage({
    item: LocalStorageKey.token,
  });
  if (legacyToken) {
    utilitiesService.setLocalStorage({ item: tokenKey, value: legacyToken });
    utilitiesService.removeLocalStorage({ item: LocalStorageKey.token });
    return legacyToken;
  }

  return null;
};

export const Logout = () => {
  signOut();
};

export const validatePassword = (form) => {
  return axiosPost(form, validatePasswordURL, {
    hide_success_toaster: true,
  });
};

export const changePassword = (form) => {
  return axiosPatch(changePasswordUrl, form, {
    hide_success_toaster: true,
  });
};

export const setRegistrationData = (data) => {
  let regData = data;
  if (typeof data?.investorType == "object") {
    regData.investorType = data?.investorType?.name;
  }
  if (typeof data?.nationality == "object") {
    regData.nationality = data?.nationality?.name;
  }
  if (typeof data?.addressCountry == "object") {
    regData.addressCountry = data?.addressCountry?.name;
  }
  localStorage.setItem(
    LocalStorageKey.registrationData,
    JSON.stringify(regData)
  );
  Observables.registrationData$.next(regData);
};

export const getRegistrationData = () => {
  let regData = localStorage.getItem(LocalStorageKey.registrationData)
    ? JSON.parse(localStorage.getItem(LocalStorageKey.registrationData))
    : null;
  if (typeof regData?.investorType == "object") {
    regData.investorType = regData?.investorType?.name;
  }
  if (typeof regData?.nationality == "object") {
    regData.nationality = regData?.nationality?.name;
  }
  if (typeof regData?.addressCountry == "object") {
    regData.addressCountry = regData?.addressCountry?.name;
  }
  return regData;
};

export const getRegistrationInfo = () => {
  return localStorage.getItem(LocalStorageKey.registrationInfo)
    ? JSON.parse(localStorage.getItem(LocalStorageKey.registrationInfo))
    : null;
};

export const setRegistrationInfo = (response) => {
  let registrationInfo = {
    borrowerStep: null,
    investorStep: null,
    investorType: null,
  };
  if (response) {
    registrationInfo = {
      borrowerStep: response?.borrowerStep,
      investorStep: response?.investorStep,
      investorType: response?.investorType,
    };
    localStorage.setItem(
      LocalStorageKey.registrationInfo,
      JSON.stringify(registrationInfo)
    );
  }
};

export const updateInvestorSteps = (step) => {
  let registrationInfo = {
    borrowerStep: null,
    investorStep: null,
    investorType: null,
  };
  if (getRegistrationInfo()) {
    registrationInfo = getRegistrationInfo();
    registrationInfo.investorStep = step;
  }
  setRegistrationInfo(registrationInfo);
};

export const updateBorrowerSteps = (step) => {
  let registrationInfo = {
    borrowerStep: null,
    investorStep: null,
    investorType: null,
  };
  if (getRegistrationInfo()) {
    registrationInfo = getRegistrationInfo();
    registrationInfo.borrowerStep = step;
  }
  setRegistrationInfo(registrationInfo);
};
